import type { IngestionPolicy, KnowledgeBase } from '@ralph/contracts'
import { Info, RefreshCw } from 'lucide-react'
import { useId, useState } from 'react'
import { Button, ChoiceRow, Readout } from '@/components/ui/primitives.tsx'
import { useReadingCapabilities } from '@/hooks/use-api.ts'
import { useReprocessBase, useUpdateKnowledgeBase } from '@/hooks/use-knowledge-bases.ts'
import type { Messages } from '@/i18n/index.ts'
import { useI18n } from '@/i18n/index.ts'
import { asSentence, messageOf } from './knowledge-viewer.ts'

/**
 * How a knowledge base reads pictures and scans (PRD-24 ING-015), and reading again what an
 * older reader read (PRD-24 D-7).
 *
 * Read-only for everyone who can use the base; its maintainers get "Change…", which opens the
 * three choices plus the languages this deployment can read. The languages come from the API,
 * which refuses one the deployment does not carry, so the list here is the list that can be saved.
 */

type ReadingCopy = Messages['knowledge']['reading']

/**
 * The three choices, built from the ACTIVE catalogue rather than captured beside it.
 *
 * It used to be a module-scope constant spread out of `t.choices`, which is the derived-capture
 * half of PRD-27 F-3: the labels were resolved at import and a locale switch left them in the
 * previous language with no type error, no runtime error and no failing test.
 */
const ocrChoices = (t: ReadingCopy): Array<{ value: IngestionPolicy['ocr']; label: string; hint: string }> => [
  { value: 'auto', ...t.choices.auto },
  { value: 'always', ...t.choices.always },
  { value: 'off', ...t.choices.off }
]

/**
 * The policy in the words the choices use to set it.
 *
 * Pure, so the words arrive as a parameter (LOC-012). The language codes inside the brackets are
 * identifiers the deployment carries and are never translated (D-17).
 */
export const describeOcrPolicy = (t: ReadingCopy, policy: IngestionPolicy | undefined = { ocr: 'auto' }): string => {
  const languages = policy.ocrLanguages && policy.ocrLanguages.length > 0 ? ` (${policy.ocrLanguages.join(', ')})` : ''
  switch (policy.ocr) {
    case 'always':
      return t.describe.always(languages)
    case 'off':
      return t.describe.off
    default:
      return t.describe.auto(languages)
  }
}

export const ReadingSettings = ({ base, canMaintain }: { base: KnowledgeBase; canMaintain: boolean }) => {
  const knowledge = useI18n().t.knowledge
  const t = knowledge.reading
  const update = useUpdateKnowledgeBase()
  const reading = useReadingCapabilities(canMaintain)
  const policy = base.ingestionPolicy ?? { ocr: 'auto' as const }
  const [editing, setEditing] = useState(false)
  const [ocr, setOcr] = useState<IngestionPolicy['ocr']>(policy.ocr)
  const [languages, setLanguages] = useState<string[]>(policy.ocrLanguages ?? [])
  const fieldId = useId()

  if (!editing) {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <Readout label={knowledge.base.pictures} value={describeOcrPolicy(t, policy)} />
        {canMaintain && (
          <Button
            size="sm"
            variant="ghost"
            aria-label={`${t.change} ${t.title}`}
            onClick={() => {
              setOcr(policy.ocr)
              setLanguages(policy.ocrLanguages ?? [])
              setEditing(true)
            }}
          >
            {t.change}
          </Button>
        )}
      </span>
    )
  }

  const available = reading.data?.ocr.languages ?? []
  const noEngine = reading.data?.ocr.engine === 'none'

  return (
    <form
      aria-labelledby={`${fieldId}-title`}
      className="w-full rounded-tile border border-rule bg-card-solid/55 px-3.5 py-3"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !update.isPending) {
          event.stopPropagation()
          setEditing(false)
        }
      }}
      onSubmit={(event) => {
        event.preventDefault()
        update.mutate(
          {
            baseId: base.id,
            ingestionPolicy: { ocr, ...(languages.length > 0 ? { ocrLanguages: languages } : {}) }
          },
          { onSuccess: () => setEditing(false) }
        )
      }}
    >
      <span id={`${fieldId}-title`} className="eyebrow mb-1.5 block text-fg-muted">
        {t.title}
      </span>
      {noEngine && (
        <p className="mb-2 flex items-start gap-1.5 text-pretty text-[12.5px] leading-[1.45] text-fg-muted">
          <Info size={12} strokeWidth={2.2} className="mt-[3px] shrink-0" aria-hidden />
          {t.noEngine}
        </p>
      )}
      <ChoiceRow
        name={`reading-${base.id}`}
        value={ocr}
        disabled={update.isPending}
        options={ocrChoices(t)}
        onChange={setOcr}
      />
      {ocr !== 'off' && available.length > 0 && (
        <fieldset className="mt-3">
          <legend className="eyebrow mb-1.5 block text-fg-muted">{t.languages}</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {available.map((code) => (
              <label key={code} className="flex items-center gap-1.5 text-[12.5px]">
                <input
                  type="checkbox"
                  checked={languages.includes(code)}
                  disabled={update.isPending}
                  onChange={(event) =>
                    setLanguages(event.target.checked ? [...languages, code] : languages.filter((l) => l !== code))
                  }
                />
                {code}
              </label>
            ))}
          </div>
          <p className="mt-1.5 text-[12px] leading-[1.45] text-fg-subtle">{t.languagesHint}</p>
        </fieldset>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" variant="primary" disabled={update.isPending}>
          {update.isPending ? t.saving : t.save}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={update.isPending}>
          {t.cancel}
        </Button>
        {update.isError && (
          <span role="alert" className="text-[12.5px] text-alert">
            {asSentence(messageOf(update.error, knowledge.genericError))}
          </span>
        )}
      </div>
      <p className="mt-2 text-[12px] leading-[1.45] text-fg-subtle">{t.appliesFrom}</p>
    </form>
  )
}

/**
 * Documents read by an older pipeline than the one now deployed (PRD-24 D-7). A control, not an
 * automatic re-read: an upgrade is something a maintainer evaluates on a few documents first. The
 * status region is mounted empty and outside the count guard, because the count drops to zero the
 * moment the documents are queued — which would unmount the announcement mid-sentence.
 */
export const ReprocessOutdated = ({ base }: { base: KnowledgeBase }) => {
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const reprocess = useReprocessBase(base.id)
  return (
    <span className="flex flex-wrap items-center gap-2">
      {base.outdatedCount > 0 && (
        <Button
          size="sm"
          variant="ghost"
          icon={<RefreshCw size={12} aria-hidden />}
          disabled={reprocess.isPending}
          onClick={() => reprocess.mutate(false)}
        >
          {reprocess.isPending ? t.outdated.queueing : t.outdated.readAgain(fmt, base.outdatedCount)}
        </Button>
      )}
      <span role="status" className="readout text-fg-subtle">
        {reprocess.isSuccess ? t.outdated.queued(fmt, reprocess.data.queued) : ''}
      </span>
      {reprocess.isError && (
        <span role="alert" className="text-[12.5px] text-alert">
          {asSentence(messageOf(reprocess.error, t.genericError))}
        </span>
      )}
    </span>
  )
}
